import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import AppErrorMessage from './AppErrorMessage'
import AppError, { ERROR_DISPLAY_METHOD, ERROR_TYPE, logAppError } from 'lib/errors'
import { Context } from 'components/AppContext'
import AppErrorPage from './AppErrorPage/AppErrorPage'
import ChunkError from './ChunkError'

const enableLogging = Object.freeze({
    [ERROR_TYPE.INTERNAL]: [
        1000,
    ],
    [ERROR_TYPE.MY_NETTIX]: [
        10011,
        10025,
        10054,
        10055,
        10056,
        10058,
    ],
})

const ErrorComponent = ({
    error,
    contentOverride,
    container,
    noRender,
    render,
    errorPage = 'auto',
    isHandledChunkError,
}) => {
    const context = useContext(Context)
    const appError = AppError.readFromApolloError(error)
    let sentryEventId = null

    if (enableLogging[appError.type] && enableLogging[appError.type].includes(appError.code)) {
        sentryEventId = logAppError(error, appError)
    }

    if (appError.message.includes('[HTTP 400]')) {
        sentryEventId = logAppError(error, appError)
    }

    // Temporary debug logging for "403: Unauthorized access: trying to get invalid bos"
    // Related ticket: https://trello.com/c/oBBuouWk/1835-investigate-unauthorized-access-trying-to-get-invalid-bos-why-and-how-shows-to-user
    if (appError.type === ERROR_TYPE.NETWORK && appError.code === 403 && appError.message && appError.message.indexOf('Unauthorized access: trying to get invalid bos') !== -1) {
        sentryEventId = logAppError(error, appError)
    }

    // Temporary debug logging for "403: Payment contract signed already (person registered to payment service), cannot be edited anymore."
    // Related ticket: https://trello.com/c/YELZodtA/2242-need-to-make-sure-that-paymentcontract-cant-be-changed-later-on
    if (appError.type === ERROR_TYPE.NETWORK && appError.code === 403 && appError.message && appError.message.indexOf('Payment contract signed already (person registered to payment service), cannot be edited anymore.') !== -1) {
        sentryEventId = logAppError(error, appError)
    }

    if (!isHandledChunkError && [ERROR_TYPE.WEBPACK_JS_CHUNK, ERROR_TYPE.WEBPACK_CSS_CHUNK].includes(appError.type)) {
        return <ChunkError originalError={error} />
    }

    if (appError.displayMethod === ERROR_DISPLAY_METHOD.NOTIFY) {
        return null
    }

    // render error page if error occurs in main context, eg. in "view root".
    // override this context based auto behaviour by setting errorPage={false}
    if (errorPage === 'auto') {
        errorPage = !!(context && context.length <= 2 && get(context, '[0]') === 'main')
    }

    if (noRender) return null

    const renderDefault = () => {
        if (errorPage && [ERROR_TYPE.NETWORK, ERROR_TYPE.MY_NETTIX, ERROR_TYPE.INTERNAL].includes(appError.type)) {
            return (
                <AppErrorPage
                    originalError={error}
                    appError={appError}
                    sentryEventId={sentryEventId}
                    contentOverride={contentOverride}
                    container={container}
                />
            )
        } else {
            return (
                <AppErrorMessage
                    originalError={error}
                    appError={appError}
                    sentryEventId={sentryEventId}
                    contentOverride={contentOverride}
                    container={container}
                />
            )
        }
    }

    const customRender = render && render({ ...appError }, renderDefault)

    return customRender || renderDefault()
}

ErrorComponent.propTypes = {
    error: PropTypes.any,
    contentOverride: PropTypes.func, // use "contentOverride" when you want to override content within the error component
    render: PropTypes.func, // use "render" when you want to render completely custom error component and not just the contents
    noRender: PropTypes.bool, // hide error from user, but trigger logging etc.
    container: PropTypes.bool,
    errorPage: PropTypes.oneOf(['auto', true, false]),
    isHandledChunkError: PropTypes.bool,
}

export default React.memo(ErrorComponent)
